import { useAlertStore } from '~~/stores/alert'
import type { WaitlistDto } from '~~/types/waitlist'
import custOFetch from '~/composables/useCustomFetch'
import type { InviteDto } from '~/types/invite'
import { useApi } from '../useApi'

export const useInvite = () => {
  const { captureException } = useSentry()
  const alertStore = useAlertStore()
  const config = useRuntimeConfig()
  const { get, post } = useApi()

  const create = async (
    payload: String,
  ) => {
    try {
      const data: any = await post(`${config.public.apiBase}/user-invite`, {
        method: 'POST',
        credentials: 'include',
        body: payload,
      })
      return data
    }
    catch (error) {
      captureException(error)
      window.console.log(error)
    }
  }

  const createAll = async (
    payload: any,
  ) => {
    try {
      const data: any = await custOFetch(`${config.public.apiBase}/user-invite/all`, {
        method: 'POST',
        credentials: 'include',
        body: payload,
      })
      return data
    }
    catch (error) {
      captureException(error)
      window.console.log(error)
    }
  }

  const invite = async (
    email: String,
  ) => {
    try {
      const payload = {
        email,
      }
      const data: any = await custOFetch(`${config.public.apiBase}/user-invite`, {
        method: 'POST',
        credentials: 'include',
        body: payload,
      })
      alertStore.setAlert(`Successfully invited ${data.email}`, 'success')
      return data
    }
    catch (error) {
      captureException(error)
      window.console.log(error)
    }
  }

  const getAll = async (currentYear: boolean = true) => {
    try {
      const url = currentYear
        ? `${config.public.apiBase}/user-invite?currentYear=true`
        : `${config.public.apiBase}/user-invite`
      const data: any = await get(url, {
        credentials: 'include',
      })
      return data
    }
    catch (error) {
      captureException(error)
      window.console.log(error)
    }
  }

  const getById = async (id: number) => {
    try {
      const data: any = await custOFetch(`${config.public.apiBase}/user-invite/${id}`, {
        method: 'GET',
        credentials: 'include',
      })
      if (data)
        alertStore.setAlert(`deleted invite ${data.id}`, 'success')
      return data
    }
    catch (error) {
      captureException(error)
      window.console.log(error)
    }
  }

  const update = async (
    id: number,
    payload: WaitlistDto,
  ) => {
    const data: any = await custOFetch(`${config.public.apiBase}/user-invite/${id}`, {
      method: 'PATCH',
      credentials: 'include',
      body: payload,
    })
    return data
  }

  const deleteById = async (id: string) => {
    console.log('Deleting invite with id', id)

    const data: any = await custOFetch(`${config.public.apiBase}/user-invite/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    })

    alertStore.setAlert(data ? 'Successfully deleted' : 'There was an error while deleting', data ? 'success' : 'error')

    return data
  }

  const resendById = async (id: string) => {
    return await custOFetch(`${config.public.apiBase}/user-invite/resend/${id}`, {
      method: 'POST',
      credentials: 'include',
    })
  }

  const acceptInvite = async (
    payload: InviteDto,
  ) => {
    const data: any = await custOFetch(`${config.public.apiBase}/user-invite/accept`, {
      method: 'PATCH',
      credentials: 'include',
      body: payload,
    })
    return data
  }

  const checkInviteStatus = async (email: string, token: string) => {
    try {
      const data: any = await custOFetch(`${config.public.apiBase}/auth/check/${email}/${token}`, {
        method: 'GET',
      }, false)

      return data
    }
    catch (error) {
      captureException(error)
      window.console.log(error)
      return null
    }
  }

  return {
    create,
    createAll,
    getAll,
    getById,
    update,
    deleteById,
    invite,
    resendById,
    acceptInvite,
    checkInviteStatus
  }
}
